import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'

/** Rich markdown renderer for user-authored character descriptions.
 *
 *  Full pipeline: GFM + line breaks, raw HTML parsed then sanitized with a
 *  permissive schema (styling allowed, scripts/protocols blocked), after
 *  preprocessing that keeps blank lines from terminating HTML blocks. */

/**
 * Preprocess markdown to prevent CommonMark HTML block termination at blank lines.
 *
 * CommonMark HTML blocks (type 6) end at blank lines, not at closing tags.
 * This causes blank lines between HTML elements (like <a> tags) to terminate
 * the block, making subsequent indented content render as code blocks.
 *
 * This function:
 * 1. Extracts <style> blocks into placeholders (preserves CSS blank lines)
 * 2. Collapses blank lines between HTML tags
 * 3. Restores <style> blocks
 */
export function preprocessMarkdown(markdown: string): string {
  if (!markdown || !markdown.includes('<')) return markdown

  // Step 1: Extract <style> blocks to protect their content from blank-line removal
  const styleBlocks: string[] = []
  let processed = markdown.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match) => {
    styleBlocks.push(match)
    return `<!--STYLE_BLOCK_PLACEHOLDER_${styleBlocks.length - 1}-->`
  })

  // Step 2: Collapse blank lines between HTML tags.
  // Matches: any tag, then one or more blank lines, then the next tag.
  // This prevents CommonMark from ending the HTML block at the blank line.
  processed = processed.replace(
    /(<\/?[a-zA-Z][^>]*>)\s*\n(\s*\n)+(?=\s*<\/?[a-zA-Z])/g,
    '$1\n',
  )

  // Step 3: Restore <style> blocks
  for (let i = 0; i < styleBlocks.length; i++) {
    processed = processed.replace(`<!--STYLE_BLOCK_PLACEHOLDER_${i}-->`, styleBlocks[i]!)
  }

  return processed
}

/**
 * Sanitization schema for user-generated HTML content
 *
 * Allows: All HTML except scripts/iframes/forms
 * Allows: All CSS (inline styles and style blocks)
 * Allows: Classes, styles, id attributes, data-* attributes
 * Blocks: Scripts, event handlers, data URIs, javascript: protocols
 */
export const sanitizeSchema: typeof defaultSchema = {
  ...defaultSchema,
  clobberPrefix: 'user-content-',
  clobber: [],
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    'style',
    // Block-level elements
    'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'pre', 'code', 'hr', 'section', 'article', 'nav',
    // Inline elements
    'strong', 'em', 'b', 'i', 'u', 's', 'sub', 'sup', 'small',
    'a', 'img', 'br', 'wbr',
    // Semantic and styling elements
    'details', 'summary', 'mark', 'del', 'ins', 'figure', 'figcaption',
    // Audio/media elements
    'audio', 'source',
  ],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    style: ['media', 'type', 'nonce', 'className', 'id', 'class', 'data-*'],
    img: ['src', 'alt', 'title', 'width', 'height', 'className', 'style', 'id', 'class', 'loading'],
    a: ['href', 'title', 'target', 'rel', 'className', 'style', 'id', 'class', 'data-*'],
    div: [...(defaultSchema.attributes?.div ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    span: [...(defaultSchema.attributes?.span ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    p: [...(defaultSchema.attributes?.p ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    h1: [...(defaultSchema.attributes?.h1 ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    h2: [...(defaultSchema.attributes?.h2 ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    h3: [...(defaultSchema.attributes?.h3 ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    h4: [...(defaultSchema.attributes?.h4 ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    h5: [...(defaultSchema.attributes?.h5 ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    h6: [...(defaultSchema.attributes?.h6 ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    ul: [...(defaultSchema.attributes?.ul ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    ol: [...(defaultSchema.attributes?.ol ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    li: [...(defaultSchema.attributes?.li ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    table: [...(defaultSchema.attributes?.table ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    th: [...(defaultSchema.attributes?.th ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    td: [...(defaultSchema.attributes?.td ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    tr: [...(defaultSchema.attributes?.tr ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    thead: [...(defaultSchema.attributes?.thead ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    tbody: [...(defaultSchema.attributes?.tbody ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    blockquote: [...(defaultSchema.attributes?.blockquote ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    code: [...(defaultSchema.attributes?.code ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    pre: [...(defaultSchema.attributes?.pre ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    hr: [...(defaultSchema.attributes?.hr ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    strong: [...(defaultSchema.attributes?.strong ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    em: [...(defaultSchema.attributes?.em ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    section: [...(defaultSchema.attributes?.section ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    article: [...(defaultSchema.attributes?.article ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    nav: [...(defaultSchema.attributes?.nav ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    details: [...(defaultSchema.attributes?.details ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    summary: [...(defaultSchema.attributes?.summary ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    mark: [...(defaultSchema.attributes?.mark ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    del: [...(defaultSchema.attributes?.del ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    ins: [...(defaultSchema.attributes?.ins ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    figure: [...(defaultSchema.attributes?.figure ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    figcaption: [...(defaultSchema.attributes?.figcaption ?? []), 'className', 'style', 'id', 'class', 'data-*'],
    audio: ['src', 'controls', 'autoPlay', 'loop', 'muted', 'preload', 'className', 'style', 'id', 'class', 'data-*'],
    source: ['src', 'type', 'media', 'className', 'style', 'id', 'class', 'data-*'],
  },
  // Block data URIs and javascript: protocols
  protocols: {
    href: ['http', 'https', 'mailto'],
    src: ['http', 'https'],
  },
}

/** Component overrides so user content behaves inside the page: links open in
 *  a new tab, images never overflow the column, audio gets a sane default. */
const previewComponents: Components = {
  a: ({ href = '', children }) => (
    <a href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ),
  img: ({ src, alt = '' }) => (
    <img src={src} alt={alt} loading="lazy" referrerPolicy="no-referrer" className="max-w-full" />
  ),
  audio: (props) => <audio {...props} controls className="w-full" />,
}

export function RichMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      components={previewComponents}
    >
      {preprocessMarkdown(children)}
    </ReactMarkdown>
  )
}
